import React from "react";
import { Switch, Redirect } from "react-router-dom";
import { Route } from "react-router";
import { useSelector } from "react-redux";
import lazyLoad from "../../utils/lazyLoad";
import { USER_TYPES } from "../config/constants";

// Route Wrappers
import {
  GuestRoute,
  PublicRoute,
  EmptyRoute,
  ProtectedRoute,
  ProtectedSupportRoute,
  ErrorPageRoute,
} from "../routing/RouteWrappers";

// Non-lazy loaded components (frequently used, loaded eagerly)
import Clip from "../../pages/modules/Clip/Clip";
import UploadClip from "../../pages/modules/Clip/UploadClip";
import UploadStories from "../../pages/modules/Stories/UploadStories";
import StoriesComponent from "../../pages/modules/Stories/Stories";
import CreatorStories from "../../pages/modules/Stories/CreatorStories";
import SingleClip from "../../pages/modules/Clip/SingleClip";
import SingleStory from "../../pages/modules/Stories/SingleStory";

// Lazy-loaded Pages - Auth
const Login = lazyLoad(() => import("../../pages/modules/Auth/Login"));
const Register = lazyLoad(() => import("../../pages/modules/Auth/Register"));
const ForgotPassword = lazyLoad(() => import("../../pages/modules/Auth/ForgotPassword"));
const EmailVerification = lazyLoad(() => import("../../pages/modules/Auth/EmailVerification"));
const ResetPassword = lazyLoad(() => import("../../pages/modules/Auth/ResetPassword"));

// Lazy-loaded Pages - Core
const Home = lazyLoad(() => import("../../pages/modules/Home/Home"));
const Settings = lazyLoad(() => import("../../pages/modules/Settings/Settings"));
const ProfileNew = lazyLoad(() => import("../../pages/modules/Account/ProfileNew"));
const CreatorProfileNew = lazyLoad(() => import("../../pages/modules/Account/CreatorProfileNew"));
const Notification = lazyLoad(() => import("../../pages/modules/Notification/Notification"));
const NotFound = lazyLoad(() => import("../../pages/modules/NotFound/NotFound"));

// Lazy-loaded Pages - Web/Legal
const Faq = lazyLoad(() => import("../../pages/modules/web/pages/Faq"));
const Terms = lazyLoad(() => import("../../pages/modules/web/pages/Term"));
const TermsUser = lazyLoad(() => import("../../pages/modules/web/pages/TermsUser"));
const TermsCreator = lazyLoad(() => import("../../pages/modules/web/pages/TermsCreator"));
const TermsUserCreator = lazyLoad(() => import("../../pages/modules/web/pages/TermsUserCreator"));
const AML = lazyLoad(() => import("../../pages/modules/web/pages/AMLPolicy"));
const Refund = lazyLoad(() => import("../../pages/modules/web/pages/RefundPolicy"));
const Privacy = lazyLoad(() => import("../../pages/modules/web/pages/Privacy"));
const Mission = lazyLoad(() => import("../../pages/modules/web/pages/OurMission"));
const Complaint = lazyLoad(() => import("../../pages/modules/web/pages/Complaint"));
const Cookie = lazyLoad(() => import("../../pages/modules/web/pages/Complaint"));

// Lazy-loaded Pages - Content
const Queue = lazyLoad(() => import("../../pages/modules/Queue/Queue"));
const Bookmarks = lazyLoad(() => import("../../pages/modules/Bookmarks/Bookmarks"));
const MyPurchases = lazyLoad(() => import("../../pages/modules/MyPurchases/MyPurchases"));
const EditPost = lazyLoad(() => import("../../pages/modules/Post/EditPost"));
const SinglePost = lazyLoad(() => import("../../pages/modules/Post/SinglePost"));
const Archive = lazyLoad(() => import("../../pages/modules/Archive/Archive"));
const CollectionsList = lazyLoad(() => import("../../pages/modules/Collection/CollectionsList"));
const CollectionDetails = lazyLoad(() => import("../../pages/modules/Collection/CollectionDetails"));
const FindACreator = lazyLoad(() => import("../../pages/modules/FindCreator/FindCreator"));

// Lazy-loaded Pages - Subscription & Payment
const SubscriptionPlans = lazyLoad(() => import("../../pages/modules/SubscriptionPlans/SubscriptionPlans"));
const CheckoutPage = lazyLoad(() => import("../../pages/modules/SubscriptionPlans/CheckoutPage"));
const CardManagement = lazyLoad(() => import("../../pages/modules/SubscriptionPlans/CardManagement"));
const SubscriptionManagement = lazyLoad(() => import("../../pages/modules/SubscriptionPlans/SubscriptionManagement"));
const Payment = lazyLoad(() => import("../../pages/modules/Payment/Payment"));

// Lazy-loaded Pages - Chat & Support
const Chats = lazyLoad(() => import("../../pages/modules/Chat/Chats"));
const Support = lazyLoad(() => import("../../pages/modules/Support/Support"));
const SupportCreate = lazyLoad(() => import("../../pages/modules/Support/SupportCreate"));
const SupportDetail = lazyLoad(() => import("../../pages/modules/Support/SupportDetail"));

const Main = () => {
  const loggedIn = useSelector((state) => state.authentication.loggedIn);
  const isCreator = useSelector(
    (state) => state.authentication?.userData?.userType === USER_TYPES.CREATOR
  );

  return (
    <div>
      <Switch>
        <PublicRoute
          exact={true}
          path="/email-verification"
          component={EmailVerification}
        />
        <EmptyRoute exact={true} path="/not-found" component={NotFound} />
        {!loggedIn ? (
          <GuestRoute exact={true} path="/" component={Login} loggedIn={loggedIn} />
        ) : (
          <ProtectedRoute
            isForCreator={false}
            exact={true}
            path="/"
            component={Home}
            loggedIn={loggedIn}
            isCreator={isCreator}
          />
        )}
        <GuestRoute exact={true} path="/register" component={Register} loggedIn={loggedIn} />
        <GuestRoute
          exact={true}
          path="/forgot-password"
          component={ForgotPassword}
          loggedIn={loggedIn}
        />
        <GuestRoute
          exact={true}
          path="/reset-password"
          component={ResetPassword}
          loggedIn={loggedIn}
        />

        <EmptyRoute exact={true} path="/terms-conditions" component={Terms} />
        <EmptyRoute exact={true} path="/privacy-policy" component={Privacy} />
        <EmptyRoute exact={true} path="/mission" component={Mission} />
        <EmptyRoute exact={true} path="/cookie-policy" component={Cookie} />
        <EmptyRoute exact={true} path="/aml-policy" component={AML} />
        <EmptyRoute exact={true} path="/refund-policy" component={Refund} />
        <EmptyRoute exact={true} path="/complaint" component={Complaint} />
        <EmptyRoute exact={true} path="/terms-user" component={TermsUser} />
        <EmptyRoute exact={true} path="/faq" component={Faq} />

        <EmptyRoute
          exact={true}
          path="/terms-creator"
          component={TermsCreator}
        />
        <EmptyRoute
          exact={true}
          path="/terms-user-creator"
          component={TermsUserCreator}
        />

        <ProtectedRoute
          isForCreator={false}
          path="/settings"
          component={Settings}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/profile"
          component={ProfileNew}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/story"
          component={SingleStory}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/queue"
          component={Queue}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/edit-post"
          component={EditPost}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/upload-stories"
          component={UploadStories}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/stories"
          component={StoriesComponent}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/creator-stories/:id"
          component={CreatorStories}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/bookmarks"
          component={Bookmarks}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/my-purchases"
          component={MyPurchases}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/post-detail"
          component={SinglePost}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/collection-details"
          component={CollectionDetails}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/archive"
          component={Archive}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/collection"
          component={CollectionsList}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/notifications"
          component={Notification}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/subscription-plans"
          component={SubscriptionPlans}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/checkout-page"
          component={CheckoutPage}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/find-a-creator"
          component={FindACreator}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/clips"
          component={Clip}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={true}
          exact={true}
          path="/upload-clip"
          component={UploadClip}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/single-clip"
          component={SingleClip}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/story"
          component={SingleStory}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/card-management"
          component={CardManagement}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/subscription-management"
          component={SubscriptionManagement}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedRoute
          isForCreator={false}
          path="/chats"
          component={Chats}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ProtectedSupportRoute
          exact={true}
          path="/support/create"
          component={SupportCreate}
          loggedIn={loggedIn}
        />
        <ProtectedSupportRoute
          exact={true}
          path="/support/details/:_id"
          component={SupportDetail}
          loggedIn={loggedIn}
        />
        <ProtectedSupportRoute
          exact={true}
          path="/support"
          component={Support}
          loggedIn={loggedIn}
        />
        <Route
          exact={true}
          path="/payment"
          render={(props) =>
            loggedIn ? <Payment {...props} /> : <Redirect to="/" />
          }
        />
        <ProtectedRoute
          isForCreator={false}
          exact={true}
          path="/:name"
          component={CreatorProfileNew}
          loggedIn={loggedIn}
          isCreator={isCreator}
        />
        <ErrorPageRoute path="*" component={NotFound} />
      </Switch>
    </div>
  );
};

export default Main;
